import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, Mail, MousePointerClick, Eye, Trash2, Play, Pause, Settings2, Send, Building2, Droplets, Users, Loader2, UserPlus, Flame, StickyNote, Pencil, UserCheck, Search, FlaskConical, Compass } from 'lucide-react';
import { toast } from 'sonner';

const STAGE_LABELS = { 0: 'Wachtrij', 1: 'Mail 1 verzonden', 2: 'Mail 2 verzonden', 3: 'Afgerond' };

const SEGMENTS = {
  b2b: { label: 'B2B Bouwbedrijven O-V', icon: Building2, enabledKey: 'drip_enabled', limitKey: 'daily_limit', defaultLimit: 15, sentKey: 'sent_today_b2b', stages: [1, 2, 3] },
  architect: { label: 'B2B Architecten', icon: Compass, enabledKey: 'drip_enabled_architect', limitKey: 'daily_limit_architect', defaultLimit: 15, sentKey: 'sent_today_architect', stages: [21, 22, 23] },
  reiniging: { label: 'Reinigingen (klanten)', icon: Droplets, enabledKey: 'drip_enabled_reiniging', limitKey: 'daily_limit_reiniging', defaultLimit: 10, sentKey: 'sent_today_reiniging', stages: [11, 12, 13] },
};

const matchesSegment = (lead, key) => {
  if (key === 'reiniging') return lead.segment === 'reiniging';
  if (key === 'architect') return lead.segment === 'architect';
  return lead.segment !== 'reiniging' && lead.segment !== 'architect';
};

function EmailPreview({ subject, bodyHtml, segment }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [scale, setScale] = useState(1);
  const timer = useRef(null);
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / 660));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.post('/prospecting/preview', { subject, body_html: bodyHtml, segment });
        setHtml(r.data.html);
      } catch { /* stil */ }
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer.current);
  }, [subject, bodyHtml, segment]);
  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid="email-preview">
      <div className="bg-muted/60 px-3 py-2 text-xs text-muted-foreground flex items-center justify-between border-b">
        <span className="font-medium text-foreground truncate">📬 Live voorbeeld — zo ziet de klant het</span>
        {loading && <Loader2 className="w-3 h-3 animate-spin" />}
      </div>
      <div ref={containerRef} className="overflow-hidden" style={{ height: 520 }}>
        {html ? (
          <iframe title="E-mail voorbeeld" srcDoc={html} className="border-0" sandbox=""
            style={{ width: 660, height: 520 / scale, transform: `scale(${scale})`, transformOrigin: 'top left' }} />
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Voorbeeld laden…</div>
        )}
      </div>
    </div>
  );
}

function CustomerImportDialog({ open, onOpenChange, onImported }) {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSearch('');
    api.get('/prospecting/customers-available').then(r => setCustomers(r.data)).catch(() => toast.error('Klanten laden mislukt'));
  }, [open]);

  const filtered = customers.filter(c =>
    `${c.first_name} ${c.last_name} ${c.company_name || ''} ${c.email}`.toLowerCase().includes(search.toLowerCase()));

  const toggle = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisible = filtered.length > 0 && filtered.every(c => selected.has(c.id));

  const doImport = async () => {
    setBusy(true);
    try {
      const r = await api.post('/prospecting/leads/import-customers', { customer_ids: [...selected] });
      toast.success(`${r.data.added} klanten toegevoegd aan de reinigingscampagne (${r.data.skipped} overgeslagen)`);
      onImported();
      onOpenChange(false);
    } catch { toast.error('Importeren mislukt'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle><Users className="w-5 h-5 inline mr-2" />Klanten uit CRM importeren</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Selecteer bestaande klanten voor de reinigings-dripcampagne. Klanten die al in de leadslijst staan worden niet getoond.</p>
        <div className="flex items-center gap-3">
          <Input placeholder="Zoeken op naam, bedrijf of e-mail…" value={search} onChange={e => setSearch(e.target.value)} data-testid="customer-search-input" />
          <Button variant="outline" size="sm" onClick={() => setSelected(allVisible ? new Set() : new Set(filtered.map(c => c.id)))} data-testid="select-all-customers-btn">
            {allVisible ? 'Deselecteer alles' : `Selecteer alles (${filtered.length})`}
          </Button>
        </div>
        <div className="max-h-[380px] overflow-y-auto border rounded-lg divide-y">
          {filtered.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Geen klanten gevonden</p>
          ) : filtered.map(c => (
            <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/40 cursor-pointer" data-testid={`customer-row-${c.id}`}>
              <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.first_name} {c.last_name}{c.company_name ? ` — ${c.company_name}` : ''}</p>
                <p className="text-xs text-muted-foreground truncate">{c.email}</p>
              </div>
            </label>
          ))}
        </div>
        <Button onClick={doImport} disabled={selected.size === 0 || busy} data-testid="import-customers-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
          {selected.size} klant{selected.size === 1 ? '' : 'en'} importeren
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function TestMailDialog({ open, onOpenChange, segment, segLabel }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setEmail(''); }, [open]);
  const send = async () => {
    if (!email.includes('@')) { toast.error('Geldig e-mailadres verplicht'); return; }
    setBusy(true);
    try {
      const r = await api.post('/prospecting/test-mail', { email, segment });
      toast.success(r.data.message, { duration: 8000 });
      onOpenChange(false);
    } catch (e) {
      toast.error(`Testmail mislukt: ${e.response?.data?.detail || 'onbekende fout'}`, { duration: 10000 });
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle><FlaskConical className="w-5 h-5 inline mr-2" />Testmail versturen</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Verstuurt de <strong>eerste kennismakingsmail</strong> van het segment "{segLabel}" met onderwerp-prefix [TEST]. Er wordt géén lead aangemaakt of geteld.</p>
        <div className="space-y-1">
          <Label>Naar e-mailadres</Label>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jouw@adres.be"
            onKeyDown={e => e.key === 'Enter' && send()} data-testid="test-mail-email-input" />
        </div>
        <Button onClick={send} disabled={busy || !email} data-testid="test-mail-send-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Testmail verzenden
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function LeadFormDialog({ open, onOpenChange, segment, segLabel, lead, onSaved }) {
  const empty = { first_name: '', last_name: '', company: '', description: '', email: '', phone: '' };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const isEdit = !!lead;
  useEffect(() => {
    if (open) setForm(lead ? {
      first_name: lead.first_name || '', last_name: lead.last_name || '', company: lead.company || '',
      description: lead.description || '', email: lead.email || '', phone: lead.phone || '',
    } : empty);
  }, [open, lead]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!form.email.includes('@')) { toast.error('Geldig e-mailadres verplicht'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/prospecting/leads/${lead.id}`, form);
        toast.success('Lead bijgewerkt');
      } else {
        await api.post('/prospecting/leads', { ...form, segment });
        toast.success(`Lead toegevoegd aan ${segLabel} — komt in de wachtrij`);
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle><UserPlus className="w-5 h-5 inline mr-2" />{isEdit ? `Lead bewerken — ${lead.first_name} ${lead.last_name}` : `Lead toevoegen — ${segLabel}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Voornaam</Label><Input value={form.first_name} onChange={set('first_name')} data-testid="add-lead-firstname" /></div>
          <div className="space-y-1"><Label>Naam</Label><Input value={form.last_name} onChange={set('last_name')} data-testid="add-lead-lastname" /></div>
        </div>
        <div className="space-y-1"><Label>Bedrijf</Label><Input value={form.company} onChange={set('company')} data-testid="add-lead-company" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>E-mail *</Label><Input type="email" value={form.email} onChange={set('email')} data-testid="add-lead-email" /></div>
          <div className="space-y-1"><Label>Telefoon</Label><Input type="tel" value={form.phone} onChange={set('phone')} placeholder="+32 4.." data-testid="add-lead-phone" /></div>
        </div>
        <div className="space-y-1"><Label>Beschrijving</Label><Textarea rows={2} value={form.description} onChange={set('description')} data-testid="add-lead-description" /></div>
        <Button onClick={save} disabled={busy || !form.email} data-testid="add-lead-save-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}{isEdit ? 'Opslaan' : 'Toevoegen'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function NotesDialog({ lead, onOpenChange, onSaved }) {
  const [notes, setNotes] = useState(lead?.notes || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNotes(lead?.notes || ''); }, [lead]);
  if (!lead) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/prospecting/leads/${lead.id}/notes`, { notes });
      toast.success('Notities opgeslagen');
      onSaved();
      onOpenChange(false);
    } catch { toast.error('Opslaan mislukt'); }
    setBusy(false);
  };
  return (
    <Dialog open={!!lead} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle><StickyNote className="w-5 h-5 inline mr-2" />Notities — {lead.first_name} {lead.last_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{lead.email}{lead.company ? ` • ${lead.company}` : ''}</p>
        <Textarea rows={8} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Bijv. gebeld op 21/08 — wil offerte voor reiniging in september, 24 panelen…" data-testid="lead-notes-textarea" />
        {lead.notes_updated_at && <p className="text-xs text-muted-foreground">Laatst bijgewerkt: {lead.notes_updated_at.slice(0, 16).replace('T', ' ')}{lead.notes_updated_by ? ` door ${lead.notes_updated_by}` : ''}</p>}
        <Button onClick={save} disabled={busy} data-testid="lead-notes-save-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <StickyNote className="w-4 h-4 mr-2" />}Opslaan
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [sentToday, setSentToday] = useState({ b2b: 0, reiniging: 0 });
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [notesLead, setNotesLead] = useState(null);
  const [testMailOpen, setTestMailOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [leadSearch, setLeadSearch] = useState('');
  const [leadFilter, setLeadFilter] = useState('all');
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [segment, setSegment] = useState('b2b');

  const load = useCallback(async () => {
    try {
      const [l, s, t] = await Promise.all([
        api.get('/prospecting/leads'), api.get('/prospecting/settings'), api.get('/prospecting/templates')]);
      setLeads(l.data.leads);
      setSentToday({ b2b: l.data.sent_today_b2b ?? 0, reiniging: l.data.sent_today_reiniging ?? 0, architect: l.data.sent_today_architect ?? 0 });
      setSettings(s.data); setTemplates(t.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const seg = SEGMENTS[segment];
  const segLeads = leads
    .filter(l => matchesSegment(l, segment))
    .filter(l => !leadSearch ||
      `${l.first_name} ${l.last_name} ${l.company || ''} ${l.email} ${l.phone || ''}`.toLowerCase().includes(leadSearch.toLowerCase()))
    .filter(l => {
      switch (leadFilter) {
        case 'notes': return !!(l.notes && l.notes.trim());
        case 'contacted': return (l.stage || 0) >= 1;
        case 'opened': return (l.opens || []).length > 0;
        case 'clicked': return (l.clicks || []).length > 0;
        case 'warm': return l.status === 'warm';
        case 'paused': return l.status === 'paused';
        case 'unsubscribed': return l.status === 'unsubscribed';
        default: return true;
      }
    });
  useEffect(() => { setSelected(new Set()); }, [segment]);
  const toggleSelect = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = segLeads.length > 0 && segLeads.every(l => selected.has(l.id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(segLeads.map(l => l.id)));
  const bulkDelete = async () => {
    if (!window.confirm(`${selected.size} lead${selected.size === 1 ? '' : 's'} definitief verwijderen?`)) return;
    try {
      const r = await api.post('/prospecting/leads/bulk-delete', { ids: [...selected] });
      toast.success(`${r.data.deleted} leads verwijderd`);
      setSelected(new Set());
      load();
    } catch { toast.error('Verwijderen mislukt'); }
  };
  const segTemplates = templates.filter(t => seg.stages.includes(t.stage));
  const opened = segLeads.filter(l => (l.opens || []).length > 0);
  const clicked = segLeads.filter(l => (l.clicks || []).length > 0);
  const warm = segLeads.filter(l => l.status === 'warm');
  const limit = settings?.[seg.limitKey] ?? seg.defaultLimit;

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('segment', segment === 'reiniging' ? 'reiniging' : segment === 'architect' ? 'architect' : 'prospect');
      const r = await api.post('/prospecting/leads/upload-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.added} leads toegevoegd aan ${seg.label} (${r.data.skipped} overgeslagen)`);
      load();
    } catch { toast.error('Upload mislukt'); }
    setUploading(false);
    e.target.value = '';
  };

  const toggleDrip = async (val) => {
    const r = await api.put('/prospecting/settings', { [seg.enabledKey]: val });
    setSettings(r.data);
    toast.success(val ? `Drip ${seg.label} AAN — max ${limit} kennismakingsmails/dag` : `Drip ${seg.label} gepauzeerd`);
  };

  const saveLimit = async (val) => {
    const n = parseInt(val, 10);
    if (!n || n < 1) return;
    const r = await api.put('/prospecting/settings', { [seg.limitKey]: n });
    setSettings(r.data);
  };

  const saveTemplate = async (tpl) => {
    await api.put(`/prospecting/templates/${tpl.stage}`, { subject: tpl.subject, body_html: tpl.body_html });
    toast.success(`${tpl.name} opgeslagen`);
  };

  const runNow = async () => {
    const r = await api.post('/prospecting/drip/run-now');
    const total = (r.data.sent_intro || 0) + (r.data.sent_followup || 0);
    if (total > 0) {
      toast.success(`Verzonden: ${r.data.sent_intro || 0} kennismaking, ${r.data.sent_followup || 0} opvolging`);
    } else if (r.data.errors?.length) {
      toast.error(`Verzenden mislukt: ${r.data.errors[0]}`, { duration: 10000 });
    } else {
      toast.info('Niets verzonden — geen leads in de wachtrij of daglimiet bereikt');
    }
    load();
  };

  const delLead = async (id) => { await api.delete(`/prospecting/leads/${id}`); load(); };
  const convertLead = async (l) => {
    try {
      const r = await api.post(`/prospecting/leads/${l.id}/convert`);
      toast.success(r.data.created ? 'Lead omgezet naar nieuwe klant' : 'Gekoppeld aan bestaande klant', {
        action: { label: 'Bekijk klant', onClick: () => navigate(`/klanten/${r.data.customer_id}`) },
        duration: 8000,
      });
      load();
    } catch { toast.error('Omzetten mislukt'); }
  };
  const pauseLead = async (l) => {
    await api.put(`/prospecting/leads/${l.id}/status?status=${l.status === 'paused' ? 'active' : 'paused'}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Leads — AI Prospecting</h1>
          <p className="text-sm text-muted-foreground">Drip-campagne: kennismaking → +7 dagen herinnering → +14 dagen laatste mail. Automatische verzending enkel op weekdagen 08–17u.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setTestMailOpen(true)} data-testid="test-mail-btn"><FlaskConical className="w-4 h-4 mr-2" />Testmail</Button>
          <Button variant="outline" onClick={runNow} data-testid="drip-run-now-btn"><Send className="w-4 h-4 mr-2" />Nu verzenden</Button>
        </div>
      </div>

      <Tabs value={segment} onValueChange={setSegment}>
        <TabsList>
          {Object.entries(SEGMENTS).map(([key, s]) => {
            const Icon = s.icon;
            const count = leads.filter(l => matchesSegment(l, key)).length;
            return (
              <TabsTrigger key={key} value={key} data-testid={`segment-tab-${key}`}>
                <Icon className="w-4 h-4 mr-2" />{s.label}
                <Badge variant="secondary" className="ml-2">{count}</Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value={segment} className="space-y-4 mt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={() => setAddOpen(true)} data-testid="add-lead-btn"><UserPlus className="w-4 h-4 mr-2" />Lead toevoegen</Button>
            <label className="cursor-pointer">
              <input type="file" accept=".csv" className="hidden" onChange={uploadCsv} data-testid="leads-csv-input" />
              <Button variant="outline" asChild disabled={uploading}><span><Upload className="w-4 h-4 mr-2" />CSV importeren</span></Button>
            </label>
            {segment === 'reiniging' && (
              <Button variant="outline" onClick={() => setCustOpen(true)} data-testid="import-crm-customers-btn">
                <Users className="w-4 h-4 mr-2" />Klanten uit CRM importeren
              </Button>
            )}
            <Button variant="outline" onClick={() => setTplOpen(true)} data-testid="edit-templates-btn"><Settings2 className="w-4 h-4 mr-2" />Mails bewerken + voorbeeld</Button>
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 ml-auto">
              <Label className="text-xs text-muted-foreground">Max/dag</Label>
              <Input type="number" min={1} className="w-16 h-8" defaultValue={limit} key={`${segment}-${limit}`} onBlur={e => saveLimit(e.target.value)} data-testid="daily-limit-input" />
            </div>
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
              {settings?.[seg.enabledKey] ? <Play className="w-4 h-4 text-green-500" /> : <Pause className="w-4 h-4 text-amber-500" />}
              <Label className="cursor-pointer">Drip-modus</Label>
              <Switch checked={!!settings?.[seg.enabledKey]} onCheckedChange={toggleDrip} data-testid={`drip-toggle-${segment}`} />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {[['Totaal leads', segLeads.length], ['Vandaag verzonden', `${sentToday[segment]}/${limit}`],
              ['In wachtrij', segLeads.filter(l => l.stage === 0 && l.status === 'active').length],
              ['Geopend', opened.length], ['Geklikt → bellen!', clicked.length],
              ['Warm — opvolgen', warm.length]].map(([t, v], i) => (
              <Card key={i}><CardContent className="p-4"><p className="text-sm text-muted-foreground">{t}</p><p className={`text-2xl font-bold ${i >= 4 ? 'text-green-500' : i === 3 ? 'text-amber-500' : ''}`}>{v}</p></CardContent></Card>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Zoek op naam, bedrijf, e-mail of telefoon…"
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                className="pl-10"
                data-testid="lead-search-input"
              />
              {leadSearch && (
                <button className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setLeadSearch('')} data-testid="lead-search-clear">✕</button>
              )}
            </div>
            <Select value={leadFilter} onValueChange={setLeadFilter}>
              <SelectTrigger className="w-[200px]" data-testid="lead-filter-select">
                <SelectValue placeholder="Filter leads" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle leads</SelectItem>
                <SelectItem value="notes">Met notitie</SelectItem>
                <SelectItem value="contacted">Gecontacteerd</SelectItem>
                <SelectItem value="opened">Geopend</SelectItem>
                <SelectItem value="clicked">Geklikt</SelectItem>
                <SelectItem value="warm">Warm — antwoordde</SelectItem>
                <SelectItem value="paused">Gepauzeerd</SelectItem>
                <SelectItem value="unsubscribed">Uitgeschreven</SelectItem>
              </SelectContent>
            </Select>
            {(leadSearch || leadFilter !== 'all') && (
              <span className="text-sm text-muted-foreground" data-testid="lead-search-count">{segLeads.length} resultaten</span>
            )}
          </div>

          {selected.size > 0 && (
            <div className="flex items-center gap-3 bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-2" data-testid="bulk-actions-bar">
              <span className="text-sm font-medium">{selected.size} geselecteerd</span>
              <Button variant="destructive" size="sm" onClick={bulkDelete} data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />Verwijder geselecteerde
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn">Deselecteren</Button>
            </div>
          )}

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full">
                <thead className="border-b"><tr className="text-left">
                  <th className="p-3 w-10"><Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-leads-checkbox" /></th>
                  <th className="p-3 font-medium">Naam</th><th className="p-3 font-medium">Bedrijf</th>
                  <th className="p-3 font-medium">E-mail</th><th className="p-3 font-medium">Fase</th>
                  <th className="p-3 font-medium">Opens</th><th className="p-3 font-medium">Clicks</th>
                  <th className="p-3 font-medium">Volgende mail</th><th className="p-3 font-medium text-right">Acties</th>
                </tr></thead>
                <tbody>
                  {segLeads.length === 0 ? (
                    <tr><td colSpan={9} className="p-10 text-center text-muted-foreground">
                      {segment === 'reiniging'
                        ? 'Importeer klanten uit het CRM of via CSV (kolommen: voornaam, naam, bedrijf, email)'
                        : 'Importeer een CSV met kolommen: voornaam, naam, bedrijf, beschrijving, email'}
                    </td></tr>
                  ) : segLeads.map(l => (
                    <tr key={l.id} className={`border-b last:border-0 hover:bg-muted/30 ${selected.has(l.id) ? 'bg-primary/5' : l.status === 'warm' || (l.clicks || []).length ? 'bg-green-500/5' : ''}`} data-testid={`lead-row-${l.id}`}>
                      <td className="p-3"><Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleSelect(l.id)} data-testid={`lead-checkbox-${l.id}`} /></td>
                      <td className="p-3 font-medium">{l.first_name} {l.last_name}</td>
                      <td className="p-3">{l.company}<div className="text-xs text-muted-foreground max-w-[220px] truncate">{l.description}</div>
                        {l.notes && <div className="text-xs text-amber-500 max-w-[220px] truncate italic" title={l.notes}>📝 {l.notes}</div>}</td>
                      <td className="p-3 text-sm">{l.email}{l.phone && <div className="text-xs text-muted-foreground">📞 {l.phone}</div>}</td>
                      <td className="p-3">{l.status === 'warm'
                        ? <Badge className="bg-green-600 text-white hover:bg-green-600 gap-1"><Flame className="w-3 h-3" />Warm — opvolgen</Badge>
                        : l.status === 'converted'
                        ? <Badge className="bg-blue-600 text-white hover:bg-blue-600 gap-1"><UserCheck className="w-3 h-3" />Klant</Badge>
                        : l.status === 'unsubscribed'
                        ? <Badge variant="secondary" className="text-muted-foreground">Uitgeschreven</Badge>
                        : <Badge variant={l.status === 'paused' ? 'secondary' : l.stage >= 3 ? 'outline' : 'default'}>{l.status === 'paused' ? 'Gepauzeerd' : STAGE_LABELS[l.stage]}</Badge>}</td>
                      <td className="p-3">{(l.opens || []).length > 0 && <Badge variant="outline" className="gap-1 text-amber-500 border-amber-500/40"><Eye className="w-3 h-3" />{l.opens.length}</Badge>}</td>
                      <td className="p-3">{(l.clicks || []).length > 0 && <Badge variant="outline" className="gap-1 text-green-500 border-green-500/40"><MousePointerClick className="w-3 h-3" />{l.clicks.length}</Badge>}</td>
                      <td className="p-3 text-xs text-muted-foreground">{l.next_send_at ? l.next_send_at.slice(0, 10) : l.stage === 0 ? 'in wachtrij' : '—'}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {l.status === 'converted'
                          ? <Button variant="ghost" size="icon" title="Bekijk klant" className="text-blue-500" onClick={() => navigate(`/klanten/${l.customer_id}`)} data-testid={`lead-view-customer-btn-${l.id}`}><UserCheck className="w-4 h-4" /></Button>
                          : <Button variant="ghost" size="icon" title="Omzetten naar klant" className={l.status === 'warm' ? 'text-green-500' : ''} onClick={() => convertLead(l)} data-testid={`lead-convert-btn-${l.id}`}><UserCheck className="w-4 h-4" /></Button>}
                        <Button variant="ghost" size="icon" title="Bewerken" onClick={() => setEditLead(l)} data-testid={`lead-edit-btn-${l.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" title="Notities" className={l.notes ? 'text-amber-500' : ''} onClick={() => setNotesLead(l)} data-testid={`lead-notes-btn-${l.id}`}><StickyNote className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" title={l.status === 'paused' ? 'Hervatten' : 'Pauzeren'} onClick={() => pauseLead(l)} data-testid={`lead-pause-btn-${l.id}`}>{l.status === 'paused' ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}</Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => delLead(l.id)} data-testid={`delete-lead-${l.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle><Mail className="w-5 h-5 inline mr-2" />Drip-mails bewerken — {seg.label}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Placeholders: {'{{voornaam}} {{naam}} {{bedrijf}} {{beschrijving}}'} — rechts zie je live hoe de klant de mail ontvangt (huisstijl, knop en handtekening inbegrepen).</p>
          <Tabs defaultValue={String(seg.stages[0])} key={segment}>
            <TabsList>{segTemplates.map((t, i) => <TabsTrigger key={t.stage} value={String(t.stage)} data-testid={`tpl-tab-${t.stage}`}>Mail {i + 1}</TabsTrigger>)}</TabsList>
            {segTemplates.map(t => {
              const idx = templates.findIndex(x => x.stage === t.stage);
              return (
                <TabsContent key={t.stage} value={String(t.stage)}>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-3">
                      <p className="text-sm font-medium">{t.name}</p>
                      <Label>Onderwerp</Label>
                      <Input value={t.subject} onChange={e => setTemplates(p => p.map((x, j) => j === idx ? { ...x, subject: e.target.value } : x))} data-testid={`tpl-subject-${t.stage}`} />
                      <Label>Inhoud (HTML)</Label>
                      <Textarea rows={16} value={t.body_html} onChange={e => setTemplates(p => p.map((x, j) => j === idx ? { ...x, body_html: e.target.value } : x))} data-testid={`tpl-body-${t.stage}`} />
                      <Button onClick={() => saveTemplate(templates[idx])} data-testid={`tpl-save-${t.stage}`}>Opslaan</Button>
                    </div>
                    <EmailPreview subject={t.subject} bodyHtml={t.body_html} segment={segment === 'reiniging' ? 'reiniging' : 'prospect'} />
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </DialogContent>
      </Dialog>

      <CustomerImportDialog open={custOpen} onOpenChange={setCustOpen} onImported={load} />
      <LeadFormDialog open={addOpen} onOpenChange={setAddOpen} segment={segment === 'reiniging' ? 'reiniging' : 'prospect'} segLabel={seg.label} onSaved={load} />
      <LeadFormDialog open={!!editLead} onOpenChange={(o) => !o && setEditLead(null)} lead={editLead} segLabel={seg.label} onSaved={load} />
      <NotesDialog lead={notesLead} onOpenChange={(o) => !o && setNotesLead(null)} onSaved={load} />
      <TestMailDialog open={testMailOpen} onOpenChange={setTestMailOpen} segment={segment === 'reiniging' ? 'reiniging' : 'prospect'} segLabel={seg.label} />
    </div>
  );
}
